import { useCallback, useEffect, useState } from 'react';
import { deleteAccount, fetchAccounts, impersonateAccount } from '../api.js';
import { impersonationURL } from '../config.js';
import { relativeTime } from '../../../shared/time.js';
import { useToast } from '../Toaster.jsx';

// Every account in the service. The only page that crosses the tenancy
// boundary the rest of the system is built around, which is why it is behind a
// role rather than behind a setting.
export default function AccountsPanel({ me }) {
  const [accounts, setAccounts] = useState([]);
  const notify = useToast();
  const [busy, setBusy] = useState('');
  // Which row is asking to be confirmed before it is deleted. Deleting an
  // account takes its shelves, its displays and its credentials with it and
  // there is no undo, so it does not happen on one click.
  const [confirming, setConfirming] = useState('');
  const [link, setLink] = useState(null);

  const reload = useCallback(() => {
    fetchAccounts()
      .then(setAccounts)
      .catch((error) => notify(`Could not load accounts: ${error.message}`, { tone: 'error' }));
  }, []);

  useEffect(reload, [reload]);

  async function handleImpersonate(account) {
    setBusy(account.id);
    try {
      const grant = await impersonateAccount(account.id);
      const url = impersonationURL(grant.code);
      setLink({ url, email: account.email, minutes: Math.round((grant.expiresIn || 300) / 60) });
      try {
        await navigator.clipboard.writeText(url);
        notify(`Link for ${account.email} copied. Open it in a private window.`);
      } catch {
        // Clipboard access can be refused; the link is selectable below.
        notify('Could not copy - select the link below and copy it manually.', { tone: 'error' });
      }
    } catch (error) {
      notify(`Could not create a link: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function handleDelete(account) {
    setBusy(account.id);
    try {
      await deleteAccount(account.id);
      notify(`Deleted ${account.email}.`);
    } catch (error) {
      notify(`Could not delete ${account.email}: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      setConfirming('');
      reload();
    }
  }

  return (
    <>
      <section className="panel">
        <h2>Accounts</h2>
        <p className="panel-hint">
          Every account in the service. Impersonating one creates a single-use link that
          signs the holder in as them - open it in a private window, because it replaces
          whatever session that browser already has.
        </p>

        {accounts.length === 0 ? (
          <p className="panel-empty">No accounts yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th className="numeric">Displays</th>
                  <th className="numeric">Connections</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const self = account.id === me?.account?.id;
                  return (
                    <tr key={account.id}>
                      <td>
                        {account.email}
                        {self && <span className="row-tag">you</span>}
                      </td>
                      <td>{account.role === 'siteAdmin' ? 'Site admin' : 'User'}</td>
                      <td>{relativeTime(account.createdAt)}</td>
                      <td className="numeric">{account.devices}</td>
                      <td className="numeric">{account.connections}</td>
                      <td className="row-actions">
                        <button
                          type="button"
                          onClick={() => handleImpersonate(account)}
                          disabled={busy === account.id}
                        >
                          Impersonate
                        </button>
                        {confirming === account.id ? (
                          <>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => handleDelete(account)}
                              disabled={busy === account.id}
                            >
                              {busy === account.id ? 'Deleting…' : 'Confirm delete'}
                            </button>
                            <button type="button" onClick={() => setConfirming('')}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="danger"
                            onClick={() => setConfirming(account.id)}
                            // Deleting your own account would take away the only
                            // way back to this page, so the server refuses it and
                            // the button does not pretend otherwise.
                            disabled={self}
                            title={self ? 'Sign in as another site admin to delete this account' : undefined}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {link && (
        <section className="panel">
          <div className="panel-header">
            <h2>Impersonation link</h2>
            <div className="panel-actions">
              <button type="button" onClick={() => setLink(null)}>
                Done
              </button>
            </div>
          </div>
          <p className="panel-hint">
            Signs the holder in as <strong>{link.email}</strong>. It works once and expires
            in {link.minutes} minutes. Treat it as their password until then.
          </p>
          <div className="share-link">
            <input type="text" readOnly value={link.url} onFocus={(event) => event.target.select()} />
          </div>
        </section>
      )}

    </>
  );
}
